import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import { formatLargeNumber, formatDateWithTimezone } from '../../utils/helpers';
import './ScenariosList.css';

const ScenariosList = () => {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const navigate = useNavigate();

  useEffect(() => {
    loadScenarios();
  }, []);

  const loadScenarios = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getScenarios();
      setScenarios(response.data);
    } catch (err) {
      console.error('Failed to load scenarios:', err);
      setError(err.response?.data?.detail || 'Failed to load scenarios');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteScenario = async (scenarioId) => {
    if (!window.confirm('Are you sure you want to delete this scenario? This action cannot be undone.')) {
      return;
    }

    try {
      await apiService.deleteScenario(scenarioId);
      setScenarios(prev => prev.filter(s => s._id !== scenarioId));
    } catch (err) {
      console.error('Failed to delete scenario:', err);
      alert(err.response?.data?.detail || 'Failed to delete scenario');
    }
  };

  const formatDate = (dateString, timezone = null) => {
    return formatDateWithTimezone(dateString, timezone);
  };

  const getEmissionColor = (value, max = 3000) => {
    const percentage = (value / max) * 100;
    if (percentage < 30) return '#10B981';
    if (percentage < 70) return '#F59E0B';
    return '#EF4444';
  };

  const filteredScenarios = scenarios.filter(scenario => {
    switch (filter) {
      case 'recent':
        return new Date(scenario.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      case 'high-emission':
        return scenario.emissions?.total_emissions > 1500;
      default:
        return true;
    }
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentScenarios = filteredScenarios.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredScenarios.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate stats
  const totalEmissions = scenarios.reduce((sum, s) => sum + (s.emissions?.total_emissions || 0), 0);
  const recentScenarios = scenarios.filter(s => 
    new Date(s.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
  );
  const avgEmissions = scenarios.length ? (totalEmissions / scenarios.length) : 0;

  if (loading) {
    return (
      <div className="scenarios-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading scenarios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="scenarios-container">
      {/* Page Header */}
      <div className="page-header-modern">
        <div className="header-content">
          <div className="title-section">
            <h1 className="page-title-modern">Lifecycle Assessment Scenarios</h1>
            <p className="page-subtitle-modern">Manage and analyze your complete LCA scenarios</p>
          </div>
          <button 
            onClick={() => navigate('/scenarios/create')} 
            className="btn-create-modern"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Create Scenario
          </button>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="stats-dashboard-modern">
        <div className="stat-card-modern">
          <div className="stat-icon-modern" style={{ background: '#EFF6FF' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L18.7071 8.70711C18.8946 8.89464 19 9.149 19 9.41421V19C19 20.1046 18.1046 21 17 21Z" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="stat-content-modern">
            <div className="stat-value-modern">{scenarios.length}</div>
            <div className="stat-label-modern">Total Scenarios</div>
          </div>
        </div>

        <div className="stat-card-modern">
          <div className="stat-icon-modern" style={{ background: '#F0FDF4' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="stat-content-modern">
            <div className="stat-value-modern">{recentScenarios.length}</div>
            <div className="stat-label-modern">This Week</div>
          </div>
        </div>

        <div className="stat-card-modern">
          <div className="stat-icon-modern" style={{ background: '#FEF3C7' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13 7H21M13 12H21M13 17H21M6 20V4M6 20H3L6 4M6 20H9L6 4" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="stat-content-modern">
            <div className="stat-value-modern">{formatLargeNumber(totalEmissions, { decimals: 1 })}</div>
            <div className="stat-label-modern">Total kg CO₂e</div>
          </div>
        </div>
      </div>

      {/* Filter and Controls */}
      <div className="controls-bar-modern">
        <div className="filter-section-modern">
          <label className="filter-label-modern">Filter by</label>
          <select
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select-modern"
          >
            <option value="all">All Scenarios</option>
            <option value="recent">Recent (Last 7 days)</option>
            <option value="high-emission">High Emission (&gt; 1500 kg CO₂e)</option>
          </select>
        </div>
        <div className="results-info-modern">
          Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredScenarios.length)} of {filteredScenarios.length} scenarios
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="error-banner-modern">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="error-close-modern">×</button>
        </div>
      )}

      {/* Empty State */}
      {filteredScenarios.length === 0 && !loading && (
        <div className="empty-state-modern">
          <div className="empty-icon-modern">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="8" y="16" width="48" height="40" rx="4" stroke="#94A3B8" strokeWidth="2" fill="none"/>
              <path d="M16 28H48M16 36H48M16 44H36" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <h2 className="empty-title-modern">No Scenarios Found</h2>
          <p className="empty-text-modern">
            {filter === 'all' 
              ? "Get started by creating your first lifecycle assessment scenario." 
              : "No scenarios match your current filter criteria. Try adjusting your filters."}
          </p>
          <button onClick={() => navigate('/scenarios/create')} className="btn-create-modern">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Create Your First Scenario
          </button>
        </div>
      )}

      {/* Scenarios Grid */}
      {currentScenarios.length > 0 && (
        <>
          <div className="scenarios-grid-modern">
            {currentScenarios.map((scenario) => (
              <div key={scenario._id} className="scenario-card-modern">
                {/* Card Header */}
                <div className="scenario-header-modern">
                  <div className="scenario-title-section">
                    <h3 className="scenario-name-modern">{scenario.name}</h3>
                    <span className="facility-badge-modern">{scenario.facility}</span>
                  </div>
                  <div className="scenario-meta-modern">
                    <span className="meta-text-modern">Created {formatDate(scenario.created_at, scenario.timezone)}</span>
                  </div>
                </div>

                {/* Comments Section */}
                {scenario.comments && (
                  <div className="scenario-comments-modern">
                    <p>{scenario.comments}</p>
                  </div>
                )}

                {/* Emissions Summary */}
                {scenario.emissions ? (
                  <div className="emissions-container-modern">
                    <div className="emissions-total-modern">
                      <span className="emissions-label-modern">Total Emissions</span>
                      <span 
                        className="emissions-value-modern"
                        style={{ color: getEmissionColor(scenario.emissions.total_emissions) }}
                      >
                        {formatLargeNumber(scenario.emissions.total_emissions, { decimals: 2 })} kg CO₂e
                      </span>
                    </div>
                    
                    <div className="emissions-grid-modern">
                      <div className="emission-cell-modern">
                        <div className="emission-badge-modern" style={{ background: '#DBEAFE', color: '#1E40AF' }}>A1</div>
                        <span className="emission-amount-modern">{formatLargeNumber(scenario.emissions.total_a1, { decimals: 1 })}</span>
                      </div>
                      <div className="emission-cell-modern">
                        <div className="emission-badge-modern" style={{ background: '#E0E7FF', color: '#4338CA' }}>A2</div>
                        <span className="emission-amount-modern">{formatLargeNumber(scenario.emissions.total_a2, { decimals: 1 })}</span>
                      </div>
                      <div className="emission-cell-modern">
                        <div className="emission-badge-modern" style={{ background: '#D1FAE5', color: '#065F46' }}>A3</div>
                        <span className="emission-amount-modern">{formatLargeNumber(scenario.emissions.total_a3, { decimals: 1 })}</span>
                      </div>
                      <div className="emission-cell-modern">
                        <div className="emission-badge-modern" style={{ background: '#FEE2E2', color: '#991B1B' }}>A4-A5</div>
                        <span className="emission-amount-modern">{formatLargeNumber(scenario.emissions.total_a4_a5 || 0, { decimals: 1 })}</span>
                      </div>
                      <div className="emission-cell-modern">
                        <div className="emission-badge-modern" style={{ background: '#FED7AA', color: '#9A3412' }}>B2-B5</div>
                        <span className="emission-amount-modern">{formatLargeNumber(scenario.emissions.total_b2_b5 || 0, { decimals: 1 })}</span>
                      </div>
                      <div className="emission-cell-modern">
                        <div className="emission-badge-modern" style={{ background: '#E9D5FF', color: '#6B21A8' }}>C1-C4</div>
                        <span className="emission-amount-modern">{formatLargeNumber(scenario.emissions.total_c1_c4 || 0, { decimals: 1 })}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="emissions-placeholder-modern">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="4" y="10" width="32" height="24" rx="2" stroke="#CBD5E1" strokeWidth="2" fill="none"/>
                      <path d="M12 18H28M12 22H28M12 26H20" stroke="#CBD5E1" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    <span className="placeholder-text-modern">Emissions data not yet calculated</span>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="scenario-actions-modern">
                  <button 
                    className="btn-action-modern btn-view-modern"
                    onClick={() => navigate(`/dashboard?scenario=${scenario._id}`)}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 8C1 8 3.5 3 8 3C12.5 3 15 8 15 8C15 8 12.5 13 8 13C3.5 13 1 8 1 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                    View Results
                  </button>
                  <button 
                    className="btn-action-modern btn-edit-modern"
                    onClick={() => navigate(`/scenarios/create?edit=${scenario._id}`)}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11.333 2.00004C11.5081 1.82494 11.716 1.68605 11.9447 1.59129C12.1735 1.49653 12.4187 1.44775 12.6663 1.44775C12.914 1.44775 13.1592 1.49653 13.3879 1.59129C13.6167 1.68605 13.8246 1.82494 13.9997 2.00004C14.1748 2.17513 14.3137 2.383 14.4084 2.61178C14.5032 2.84055 14.552 3.08575 14.552 3.33337C14.552 3.58099 14.5032 3.82619 14.4084 4.05497C14.3137 4.28374 14.1748 4.49161 13.9997 4.66671L5.33301 13.3334L1.66634 14.3334L2.66634 10.6667L11.333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Edit
                  </button>
                  <button 
                    className="btn-action-modern btn-delete-modern"
                    onClick={() => handleDeleteScenario(scenario._id)}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 4H14M12.6667 4V13.3333C12.6667 14 12 14.6667 11.3333 14.6667H4.66667C4 14.6667 3.33333 14 3.33333 13.3333V4M5.33333 4V2.66667C5.33333 2 6 1.33333 6.66667 1.33333H9.33333C10 1.33333 10.6667 2 10.6667 2.66667V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-modern">
              <button
                className="pagination-btn-modern"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Previous
              </button>

              <div className="pagination-numbers-modern">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        className={`pagination-number-modern ${currentPage === page ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="pagination-ellipsis-modern">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                className="pagination-btn-modern"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ScenariosList;